import { Link } from 'react-router-dom'
import { UserButton, useAuth } from '@clerk/clerk-react'
import { useDropzone } from 'react-dropzone'
import { useState } from 'react'
import { trackEvent } from '../analytics'

export default function UploadPage() {
  const { getToken } = useAuth()
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [status, setStatus] = useState<string>('')

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
  })

  const handleUpload = async () => {
    if (!file) return

    setUploading(true)
    trackEvent('file_upload', {
      file_size_mb: Number((file.size / 1024 / 1024).toFixed(2)),
    })
    try {
      const apiBase = import.meta.env.VITE_API_URL || '/api/v1'
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      setStatus('Uploading PDF...')
      const form = new FormData()
      form.append('file', file)

      const uploadRes = await fetch(`${apiBase}/statements/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      })

      if (!uploadRes.ok) {
        const err = await uploadRes.text()
        throw new Error(`Upload failed: ${err}`)
      }

      const { statementId } = await uploadRes.json()
      trackEvent('upload_success', { statement_id: statementId })

      setStatus('Processing with OCR (may take ~60–90s)...')
      const processRes = await fetch(`${apiBase}/statements/${statementId}/process`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!processRes.ok) {
        const err = await processRes.text()
        throw new Error(`Processing failed: ${err}`)
      }

      const result = await processRes.json()
      trackEvent('extract_success', {
        statement_id: statementId,
        transactions_count: result.transactions_count,
      })
      setStatus('Done!')
      alert(`Processed successfully. ${result.transactions_count} transactions extracted.`)
    } catch (error) {
      console.error('Upload failed:', error)
      trackEvent('extract_error', {
        message: String((error as Error)?.message || error).slice(0, 120),
      })
      alert('Upload failed. Please try again.')
    } finally {
      setUploading(false)
      setStatus('')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-indigo-600">
              BankStatementReader
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="px-4 py-2 text-gray-700">
                Dashboard
              </Link>
              <UserButton />
            </div>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Upload Statement</h1>
            <p className="text-gray-600 mt-2">
              Upload a bank statement PDF (max 10MB)
            </p>
          </div>

          {/* Upload Dropzone */}
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-12 text-center cursor-pointer
              transition-colors
              ${
                isDragActive
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-300 bg-white hover:border-indigo-400'
              }
            `}
          >
            <input {...getInputProps()} />
            <div className="text-6xl mb-4">📄</div>
            {isDragActive ? (
              <p className="text-lg text-indigo-600">Drop the PDF here...</p>
            ) : (
              <>
                <p className="text-lg text-gray-700 mb-2">
                  Drag and drop a PDF here, or click to browse
                </p>
                <p className="text-sm text-gray-500">Maximum file size: 10MB</p>
              </>
            )}
          </div>

          {/* Selected File */}
          {file && (
            <div className="mt-6 bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">{file.name}</div>
                  <div className="text-sm text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
                <button
                  onClick={() => setFile(null)}
                  className="text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </div>

              <button
                onClick={handleUpload}
                disabled={uploading}
                className={`
                  w-full mt-6 px-8 py-3 rounded-lg font-medium
                  ${
                    uploading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }
                  text-white
                `}
              >
                {uploading ? 'Uploading...' : 'Upload & Process'}
              </button>

              {status && (
                <div className="mt-3 text-sm text-gray-600">{status}</div>
              )}
            </div>
          )}

          {/* Instructions */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-bold text-blue-900 mb-2">How it works</h3>
            <ol className="list-decimal list-inside space-y-2 text-blue-800">
              <li>Upload your bank statement PDF</li>
              <li>Our OCR engine extracts the transactions (60-90 seconds)</li>
              <li>Review the extracted data in a spreadsheet</li>
              <li>Export to Excel or CSV</li>
            </ol>
          </div>

          {/* Usage Warning */}
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <div className="text-2xl">⚠️</div>
              <div>
                <div className="font-bold text-yellow-900 mb-1">Free Trial Limit</div>
                <div className="text-yellow-800">
                  You have <strong>5 statements</strong> remaining this month.
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
